/**
 * Map Data Utilities
 *
 * Pure functions for transforming data into map location format.
 * No Google Maps API dependency.
 */

/**
 * Resolve the best available map center from multiple sources.
 *
 * Fallback priority (most to least accurate for showing search results):
 *   1. Search location — API-provided coordinates for the searched city/area
 *   2. First facility — coordinates of actual results (best proxy when search location lacks coords)
 *   3. User geolocation — visitor's IP-based location (last resort, may be far from results)
 *
 * @param {Object} options
 * @param {Object} [options.searchLocation] - Search location with latitude/longitude
 * @param {Array}  [options.facilityCards] - Facility cards with schema.location coordinates
 * @param {Object} [options.geolocation] - User geolocation (may have string lat/lng)
 * @returns {{ latitude: number, longitude: number } | null}
 */
// A coordinate is present when it is neither null/undefined nor empty string.
// Guards against dropping a legitimate 0 (equator / prime meridian).
const hasCoords = (obj) =>
	obj?.latitude != null &&
	obj?.latitude !== "" &&
	obj?.longitude != null &&
	obj?.longitude !== "";

export function resolveMapCenter({
	searchLocation,
	facilityCards = [],
	geolocation
} = {}) {
	if (hasCoords(searchLocation)) {
		return {
			latitude: searchLocation.latitude,
			longitude: searchLocation.longitude
		};
	}

	const firstFacilityWithCoords = facilityCards.find((f) =>
		hasCoords(f.schema?.location)
	);
	if (firstFacilityWithCoords) {
		return {
			latitude: firstFacilityWithCoords.schema.location.latitude,
			longitude: firstFacilityWithCoords.schema.location.longitude
		};
	}

	if (hasCoords(geolocation)) {
		return {
			latitude: Number.parseFloat(geolocation.latitude),
			longitude: Number.parseFloat(geolocation.longitude)
		};
	}

	return null;
}

function getMapLabel(facility) {
	if (facility.lowestPrice != null) return `$${facility.lowestPrice}`;
	if (facility.startingPrice != null) return `$${facility.startingPrice}`;
	return facility.name ?? "";
}

/**
 * Convert facility cards to map location format for GoogleMap component
 *
 * @param {Array} facilityCards - Array of facility card objects
 * @returns {Array} Array of map location objects with id, latitude, longitude, and label
 */
export function facilityCardsToMapLocations(facilityCards) {
	return facilityCards
		.filter((facility) => facility.schema?.location?.latitude)
		.map((facility) => ({
			id: facility.id,
			latitude: facility.schema.location.latitude,
			longitude: facility.schema.location.longitude,
			label: getMapLabel(facility)
		}));
}
